import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { authAPI, apiClient, getApiErrorMessage } from '../services/api';

const AuthContext = createContext();

const isTokenExpired = (value) => {
  try {
    const payload = JSON.parse(atob(value.split('.')[1]));
    return payload.exp && payload.exp * 1000 <= Date.now();
  } catch (e) {
    return true;
  }
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('propnest_token'));
  const [loading, setLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    const storedToken = localStorage.getItem('propnest_token');
    if (!storedToken) return null;
    if (isTokenExpired(storedToken)) {
      setUser(null);
      setToken(null);
      localStorage.removeItem('propnest_token');
      localStorage.removeItem('propnest_user');
      return null;
    }
    try {
      const res = await apiClient.get('/api/auth/me', { _silentAuth: true });
      setUser(res.data);
      localStorage.setItem('propnest_user', JSON.stringify(res.data));
      return res.data;
    } catch (e) {
      if (e.response?.status === 401) {
        setUser(null);
        setToken(null);
        localStorage.removeItem('propnest_token');
        localStorage.removeItem('propnest_user');
      }
      return null;
    }
  }, []);

  useEffect(() => {
    // Load user from localStorage on mount, then refresh from server
    const storedUser = localStorage.getItem('propnest_user');
    if (storedUser && token) {
      setUser(JSON.parse(storedUser));
      // Best-effort refresh in background
      refreshUser();
    }
    setLoading(false);
  }, [token, refreshUser]);

  const persistSession = (accessToken, userData) => {
    setToken(accessToken);
    setUser(userData);

    localStorage.setItem('propnest_token', accessToken);
    localStorage.setItem('propnest_user', JSON.stringify(userData));
  };

  const login = async (email, password) => {
    try {
      const response = await authAPI.login(email, password);
      const { access_token, user: userData } = response.data;
      persistSession(access_token, userData);
      
      return { success: true, user: userData };
    } catch (error) {
      console.error('Login failed:', error);
      return {
        success: false,
        error: getApiErrorMessage(error, 'Login failed'),
      };
    }
  };

  const adminLogin = async (email, password) => {
    try {
      const response = await authAPI.adminLogin(email, password);
      const { access_token, user: userData } = response.data;
      persistSession(access_token, userData);

      return { success: true, user: userData };
    } catch (error) {
      console.error('Admin login failed:', error);
      return {
        success: false,
        error: getApiErrorMessage(error, 'Admin login failed'),
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await authAPI.register(userData);
      const { access_token, user: newUser } = response.data;
      
      setToken(access_token);
      setUser(newUser);
      
      localStorage.setItem('propnest_token', access_token);
      localStorage.setItem('propnest_user', JSON.stringify(newUser));
      try {
        const profile = await apiClient.get('/api/auth/me', {
          headers: { Authorization: `Bearer ${access_token}` },
          _silentAuth: true,
        });
        setUser(profile.data);
        localStorage.setItem('propnest_user', JSON.stringify(profile.data));
        return { success: true, user: profile.data };
      } catch (profileError) {
        console.warn('Profile refresh after registration failed:', profileError);
      }
      
      return { success: true, user: newUser };
    } catch (error) {
      console.error('Registration failed:', error);
      return {
        success: false,
        error: getApiErrorMessage(error, 'Registration failed'),
      };
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('propnest_token');
    localStorage.removeItem('propnest_user');
  };

  const acceptToken = async (accessToken) => {
    setToken(accessToken);
    localStorage.setItem('propnest_token', accessToken);
    const userData = await refreshUser();
    return userData;
  };

  const value = {
    user,
    token,
    loading,
    login,
    adminLogin,
    register,
    acceptToken,
    logout,
    refreshUser,
    isAuthenticated: !!token && !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
